/**
 * @file attachments-rich-text.component.tsx
 * @description Collapsed form-control компонент для работы с форматированным текстом и вложениями в свернутом состоянии. Объединяет в себе функциональность rich-text редактора (ReactQuill) и управление файловыми вложениями. Позволяет пользователям создавать и редактировать форматированный контент с возможностью прикрепления файлов. Компонент имеет режимы просмотра и редактирования, что обеспечивает экономию места в формах Task Manager.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-15
 * @dependencies ['react-quill-new', 'react', '@consta/uikit/Collapse', 'react-hook-form', '@components/ui/base/button', '@components/ui/layout/typography-container', '@src/components/ui/data/attachment-list', '@components/ui/custom/buttons/add-attach-button', 'react-quill-new/dist/quill.snow.css', 'classnames']
 * @related-files ['@components/form-controls/rich-text/rich-text.component.tsx', '@components/ui/attachment-list/attachment-list.component.tsx', '@components/ui/custom/buttons/add-attach-button/add-attach-button.component.tsx']
 */

import ReactQuill from 'react-quill-new';
import { useRef, useState } from 'react';
import { Collapse } from '@consta/uikit/Collapse';
import { Control, Controller } from 'react-hook-form';
import { Button } from '@components/ui/base/button';
import { TypographyContainer } from '@components/ui/layout/typography-container';
import { AttachmentList } from '@src/components/ui/data/attachment-list';
import { AddAttachButton } from '@components/ui/custom/buttons/add-attach-button';
import 'react-quill-new/dist/quill.snow.css';
import cn from 'classnames';

/**
 * Props для collapsed form-control компонента AttachmentsRichText с интеграцией react-hook-form
 */
interface IAttachmentsRichText {
    /** Текст заголовка для свернутой секции */
    label?: string;
    /** Control объект из react-hook-form для управления формой */
    control: Control;
    /** Имя поля в форме */
    name: string;
    /** Плейсхолдер для редактора */
    placeholder?: string;
}

export const AttachmentsRichText = ({ label, control, name, placeholder }: IAttachmentsRichText) => {
    const [isOpen, setOpen] = useState<boolean>(true);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const initialValue = useRef<string | undefined>(undefined);

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            ['link'],
            ['clean']
        ]
    };

    const formats = ['header', 'bold', 'italic', 'blockquote', 'list', 'bullet', 'indent', 'link'];

    return (
        <Collapse label={label} isOpen={isOpen} onClick={() => setOpen(!isOpen)}>
            <div className="flex flex-col gap-default">
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                        <>
                            {isEdit ? (
                                <div className="block relative h-full w-full">
                                    <ReactQuill
                                        theme="snow"
                                        value={field.value}
                                        onChange={(value: string) => {
                                            field.onChange(value);
                                        }}
                                        modules={modules}
                                        formats={formats}
                                    />

                                    <div className="absolute flex gap-default right-[1.39644vmin] bottom-[1.39644vmin]">
                                        <Button
                                            className="text-[1.482vmin]"
                                            label="Отмена"
                                            onClick={() => {
                                                field.onChange(initialValue.current);
                                                setIsEdit(false);
                                            }}
                                            view="ghost"
                                        />

                                        <Button
                                            className="text-[1.482vmin]"
                                            label="ОК"
                                            onClick={() => {
                                                field.onChange(field.value);
                                                initialValue.current = field.value;
                                                setIsEdit(false);
                                            }}
                                            view="primary"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <TypographyContainer>
                                    <span
                                        onClick={() => setIsEdit(true)}
                                        className={cn('cursor-pointer', {
                                            'text-[#3C3C4399]': !field.value,
                                            'block w-[62.49069vmin]': !field.value
                                        })}
                                        dangerouslySetInnerHTML={{ __html: field.value || placeholder || 'Нет данных' }}
                                    />
                                </TypographyContainer>
                            )}
                        </>
                    )}
                />

                <Controller
                    name="attachments"
                    control={control}
                    render={({ field }) => (
                        <div className="flex flex-col gap-default">
                            {Boolean(field.value?.length) && (
                                <AttachmentList attachments={field.value} onChange={field.onChange} />
                            )}

                            <AddAttachButton value={field.value} onChange={field.onChange} />
                        </div>
                    )}
                />
            </div>
        </Collapse>
    );
};
